import { useState, useEffect } from "react";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate, useParams } from "react-router"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import "../../../Styles/main.scss"
import { setModalData } from "../SharedSlices/ModalSlice"
import { logOut } from "../../User/UserDashboard/Logout"
import HeaderMenuItem from "./HeaderMenuItem"
import HeaderMobileProfileCard from "./HeaderMobileProfileCard"
import { RootState } from "../../../App/store"
import { profileDefault, organizationProfileDefault } from "../../../App/GlobaleVariables"
import { setRoleData } from "../SharedSlices/RolesSlice"
import SessionService from "../../../App/SessionService"
import { RoleType, StorageKey, SubscriptionType } from "../../../App/enums"
import { getUserProfileSettingsState } from "../../User/UserSettings/UserSettingsSlice"
import Image from "../Primitive/Image"
import HTTPService from "../../../App/HTTPService"
import { headers } from "../../../App/Axios"

function HeaderMobileMenu() {
    const { id } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()
    const translation = useSelector(selectTranslations)
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    let mobileMenuRef = React.useRef<HTMLInputElement>(null)
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const userDataState = useSelector((state: RootState) => state.userDataSlice)
    const organizationDataState = useSelector((state: RootState) => state.organizationsSlice.organizationSlice.organization)
    const profileImage =
        role === RoleType.USER ? userDataState.profile_photo?.url_path || profileDefault : organizationDataState.photo?.url_path || organizationProfileDefault

    useEffect(() => {
        const onMouseDownHandler = (event: Event) => {
            if (mobileMenuOpen && mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as HTMLElement)) {
                setMobileMenuOpen(false)
            }
        }
        document.addEventListener("mousedown", onMouseDownHandler)

        return () => {
            document.removeEventListener("mousedown", onMouseDownHandler)
        }
    }, [mobileMenuOpen])

    const onSettingsModalOpen = () => {
        setMobileMenuOpen(false)
        dispatch(setModalData({ open: true, settings: true }))
        dispatch(getUserProfileSettingsState({ status: true, isHashtag: false }))
    }
    const onOrganizationSettingsModalOpen = () => {
        setMobileMenuOpen(false)
        dispatch(setModalData({ open: true, organizationSettings: true }))
    }
    const onManageAdmins = () => {
        setMobileMenuOpen(false)
        const orgId = id || organizationDataState?.id || SessionService.getItem(StorageKey.organizationId)
        if (!orgId) return
        const profilePath = `/organization-profile/${orgId}`
        if (location.pathname === profilePath) {
            dispatch(setModalData({ open: true, addAdminModal: true }))
        } else {
            navigate(profilePath)
            window.setTimeout(() => {
                dispatch(setModalData({ open: true, addAdminModal: true }))
            }, 220)
        }
    }

    const onCreateRoom = () => {
        setMobileMenuOpen(false)
        const orgId = id || organizationDataState?.id || SessionService.getItem(StorageKey.organizationId)
        if (orgId) {
            HTTPService.get(`/org/${orgId}/room-limit/`, headers)
                .then(response => {
                    if (response.status === 200) {
                        dispatch(setModalData({ open: true, createRoom: true }))
                    }
                })
                .catch(error => {
                    if (error.response?.status === 400) {
                        dispatch(
                            setModalData({
                                open: true,
                                roomLimitModal: { status: true, max_rooms: error.response.data.max_rooms },
                            })
                        )
                    } else {
                        dispatch(setModalData({ open: true, createRoom: true }))
                    }
                })
        } else {
            dispatch(setModalData({ open: true, createRoom: true }))
        }
    }

    const onAddOrganizationModalOpen = () => {
        setMobileMenuOpen(false)
        dispatch(setModalData({ open: true, addOrganization: true }))
    }

    const onRouteChange = (userPath?: string, organizationPath?: string) => {
        setMobileMenuOpen(false)
        switch (role) {
            case RoleType.USER:
                navigate(userPath!)
                window.scrollTo(0, 0)
                break
            case RoleType.ORGANIZATION:
                navigate(organizationPath!)
                window.scrollTo(0, 0)
                break
            default:
                break
        }
    }

    const onReturnToUser = () => {
        setMobileMenuOpen(false)
        SessionService.setItem(StorageKey.roles, RoleType.USER)
        dispatch(setRoleData(RoleType.USER))
        navigate(`/dashboard`)
        window.scrollTo(0, 0)
    }

    const onLogOutClick = () => {
        setMobileMenuOpen(false)
        logOut()
    }

    return (
        <div ref={mobileMenuRef}>
            <div data-tutorial-anchor="header-profile-menu-mobile" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                <Image src={profileImage} alt="profile" className="image image__profile image__profile--fit image__profile--size-small" />
            </div>
            {mobileMenuOpen && (
                <div className="header-mobile-menu">
                    {role === RoleType.USER && (
                        <div onClick={() => onRouteChange("/user-profile", "")} className="header-mobile-menu__item header-mobile-menu__item--height-5-5">
                            <HeaderMobileProfileCard
                                text={translation.individual}
                                subtext={userDataState.first_name + " " + userDataState.last_name}
                                imageSrc={userDataState.profile_photo?.url_path || profileDefault}
                                subtextStyle="subtext header-mobile-profile-card__subtext--font-weight-400"
                                textStyle="text header-mobile-profile-card__text--background-color-red"
                            />
                        </div>
                    )}
                    {role === RoleType.ORGANIZATION && (
                        <div
                            onClick={() => onRouteChange("", `/organization-profile/${organizationDataState.id}`)}
                            className="header-mobile-menu__item header-mobile-menu__item--height-5-5"
                        >
                            <HeaderMobileProfileCard
                                text={translation.organization}
                                subtext={organizationDataState.name}
                                imageSrc={organizationDataState.photo?.url_path || organizationProfileDefault}
                                subtextStyle="subtext header-mobile-profile-card__subtext--font-weight-400"
                                textStyle="text header-mobile-profile-card__text--background-color-gold"
                            />
                        </div>
                    )}
                    {role === RoleType.ORGANIZATION && (
                        <div onClick={() => onReturnToUser()} className="header-mobile-menu__item header-mobile-menu__item--height-5-5">
                            <HeaderMobileProfileCard
                                text={translation.individual}
                                subtext={userDataState.first_name + " " + userDataState.last_name}
                                imageSrc={userDataState.profile_photo?.url_path || profileDefault}
                                subtextStyle="subtext header-mobile-profile-card__subtext--font-weight-400"
                                textStyle="text header-mobile-profile-card__text--background-color-red"
                            />
                        </div>
                    )}

                    {role === RoleType.USER && (
                        <HeaderMenuItem
                            onClick={() => onRouteChange("/my-organizations")}
                            title={translation.myOrganizations}
                            className="header-mobile-menu__item"
                        />
                    )}

                    {role === RoleType.USER && (
                        <HeaderMenuItem
                            onClick={onSettingsModalOpen}
                            title={translation.settings}
                            className="header-mobile-menu__item"
                        />
                    )}

                    {role === RoleType.ORGANIZATION && (
                        <>
                            <HeaderMenuItem
                                onClick={onOrganizationSettingsModalOpen}
                                title={translation.manageProfile || "Profil verwalten"}
                                className="header-mobile-menu__item"
                            />
                            <HeaderMenuItem
                                onClick={onManageAdmins}
                                title={translation.manageAdmins || "Admins verwalten"}
                                className="header-mobile-menu__item"
                            />
                            <HeaderMenuItem
                                onClick={onCreateRoom}
                                title={translation.CreateRoom || "Raum anlegen"}
                                className="header-mobile-menu__item"
                            />
                        </>
                    )}
                    {role === RoleType.USER && (
                        <HeaderMenuItem onClick={onAddOrganizationModalOpen} title={translation.addOrganization} className="header-mobile-menu__item" />
                    )}
                    <a
                        href="https://edusiia.com/faq/"
                        target="_blank"
                        rel="noreferrer"
                        className="header-mobile-menu__item"
                        onClick={() => setMobileMenuOpen(false)}
                    >
                        {" "}
                        {translation.fAQ}
                    </a>
                    <HeaderMenuItem
                        onClick={onLogOutClick}
                        title={translation.logOut}
                        className="header-mobile-menu__item header-mobile-menu__item--hover-color-red header-mobile-menu__item--text-color-white"
                    />
                </div>
            )}
        </div>
    )
}

export default HeaderMobileMenu
